import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Legend, Cell } from 'recharts';
import { BarChart3 } from 'lucide-react';
import api from '../utils/api';
import { STATUS_CONFIG, STATUS_ORDER } from '../utils/constants';

const tooltipStyle = { background: '#1c2235', border: '1px solid #252d3d', borderRadius: 8, fontFamily: 'Raleway', color: '#e8ecf4' };
const tickStyle = { fill: '#c8d0e0', fontSize: 11 };

export default function Reports() {
  const [funnel, setFunnel] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [conversion, setConversion] = useState([]);
  const [sourceData, setSourceData] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [selectedDs, setSelectedDs] = useState('');
  const [days, setDays] = useState(30);

  useEffect(() => { api.get('/datasets').then(res => setDatasets(res.data)); }, []);

  useEffect(() => {
    const params = selectedDs ? { dataset_id: selectedDs } : {};
    api.get('/reports/funnel', { params }).then(res => setFunnel(res.data));
    api.get('/reports/activity-timeline', { params: { days } }).then(res => setTimeline(res.data));
    api.get('/reports/conversion-by-dataset').then(res => setConversion(res.data));
    api.get('/reports/by-source').then(res => setSourceData(res.data)).catch(() => {});
  }, [selectedDs, days]);

  const funnelData = funnel?.funnel?.sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status))
    .map(s => ({ name: STATUS_CONFIG[s.status]?.label || s.status, value: s.count, fill: STATUS_CONFIG[s.status]?.color || '#64748b' })) || [];

  const avgTimesData = funnel?.avgTimes?.filter(t => t.avg_hours)
    .map(t => ({ name: STATUS_CONFIG[t.new_status]?.label || t.new_status, horas: Math.round(t.avg_hours * 10) / 10 })) || [];

  const channelColors = ['#2E7DE2', '#22c55e', '#f97316', '#a855f7', '#eab308', '#ef4444'];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <BarChart3 size={24} color="var(--accent)" />
        <h1 style={{ fontSize: 26, fontWeight: 800 }}>Reportes</h1>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
        <select value={selectedDs} onChange={e => setSelectedDs(e.target.value)} style={{ width: 250 }}>
          <option value="">Todas las bases</option>
          {datasets.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select value={days} onChange={e => setDays(parseInt(e.target.value))} style={{ width: 160 }}>
          <option value={7}>Últimos 7 días</option>
          <option value={14}>Últimos 14 días</option>
          <option value={30}>Últimos 30 días</option>
          <option value={60}>Últimos 60 días</option>
          <option value={90}>Últimos 90 días</option>
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, marginBottom: 20 }}>
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--text-primary)' }}>Funnel de leads</h3>
          {funnelData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={funnelData}>
                <XAxis dataKey="name" tick={tickStyle} angle={-30} textAnchor="end" height={60} />
                <YAxis tick={tickStyle} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>{funnelData.map((e, i) => <Cell key={i} fill={e.fill} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p style={{color:'var(--text-muted)'}}>Sin datos</p>}
        </div>

        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--text-primary)' }}>Tiempo promedio entre estados (horas)</h3>
          {avgTimesData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={avgTimesData} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" tick={tickStyle} />
                <YAxis dataKey="name" type="category" width={140} tick={tickStyle} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="horas" fill="var(--accent)" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p style={{color:'var(--text-muted)'}}>Sin datos de tiempos</p>}
        </div>
      </div>

      {/* Source breakdown */}
      {sourceData && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, marginBottom: 20 }}>
          <div className="card">
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--text-primary)' }}>Leads por canal</h3>
            {sourceData.bySource?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={sourceData.bySource}>
                  <XAxis dataKey="channel" tick={tickStyle} angle={-20} textAnchor="end" height={60} />
                  <YAxis tick={tickStyle} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="total" radius={[6, 6, 0, 0]}>{sourceData.bySource.map((e, i) => <Cell key={i} fill={channelColors[i % channelColors.length]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <p style={{color:'var(--text-muted)'}}>Sin datos</p>}
          </div>

          <div className="card">
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--text-primary)' }}>Conversión por canal</h3>
            {sourceData.bySource?.length > 0 ? (
              <div className="table-container">
                <table>
                  <thead><tr><th>Canal</th><th>Total</th><th>Ganados</th><th>Conversión</th></tr></thead>
                  <tbody>
                    {sourceData.bySource.map((s, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{s.channel}</td>
                        <td style={{ color: 'var(--text-primary)' }}>{s.total}</td>
                        <td style={{ color: '#22c55e', fontWeight: 600 }}>{s.ganados}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 60, height: 6, background: 'var(--bg-secondary)', borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ width: `${s.conversion_rate || 0}%`, height: '100%', background: '#22c55e', borderRadius: 3 }} />
                            </div>
                            <span style={{ fontWeight: 600, color: '#22c55e' }}>{s.conversion_rate || 0}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p style={{color:'var(--text-muted)'}}>Sin datos</p>}
          </div>
        </div>
      )}

      {/* Campaign breakdown */}
      {sourceData?.byCampaign?.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--text-primary)' }}>Rendimiento por campaña</h3>
          <div className="table-container">
            <table>
              <thead><tr><th>Campaña</th><th>Plataforma</th><th>Total</th><th>Ganados</th><th>Conversión</th></tr></thead>
              <tbody>
                {sourceData.byCampaign.map((c, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{c.campaign}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{c.utm_source}</td>
                    <td style={{ color: 'var(--text-primary)' }}>{c.total}</td>
                    <td style={{ color: '#22c55e', fontWeight: 600 }}>{c.ganados}</td>
                    <td><span style={{ fontWeight: 600, color: '#22c55e' }}>{c.conversion_rate || 0}%</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Service interest breakdown */}
      {sourceData?.byService?.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--text-primary)' }}>Leads por servicio de interés</h3>
          <div className="table-container">
            <table>
              <thead><tr><th>Servicio</th><th>Total</th><th>Ganados</th><th>Conversión</th></tr></thead>
              <tbody>
                {sourceData.byService.map((s, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{s.service}</td>
                    <td style={{ color: 'var(--text-primary)' }}>{s.total}</td>
                    <td style={{ color: '#22c55e', fontWeight: 600 }}>{s.ganados}</td>
                    <td><span style={{ fontWeight: 600, color: '#22c55e' }}>{s.conversion_rate || 0}%</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Activity Timeline */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--text-primary)' }}>Actividad en el tiempo</h3>
        {timeline && (timeline.contacts.length > 0 || timeline.newLeads.length > 0) ? (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="date" tick={tickStyle} data={[...(timeline.newLeads || []), ...(timeline.contacts || [])]} />
              <YAxis tick={tickStyle} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ color: '#c8d0e0' }} />
              <Line type="monotone" data={timeline.newLeads} dataKey="count" name="Leads nuevos" stroke="var(--accent)" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" data={timeline.contacts} dataKey="count" name="Contactos" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
              {timeline.webLeads?.length > 0 && <Line type="monotone" data={timeline.webLeads} dataKey="count" name="Leads web" stroke="#f97316" strokeWidth={2} dot={{ r: 3 }} />}
            </LineChart>
          </ResponsiveContainer>
        ) : <p style={{color:'var(--text-muted)'}}>Sin actividad en este período</p>}
      </div>

      {/* Conversion by Dataset */}
      <div className="card">
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--text-primary)' }}>Conversión por base de datos</h3>
        {conversion.length > 0 ? (
          <div className="table-container">
            <table>
              <thead><tr><th>Base de datos</th><th>Tipo</th><th>Total</th><th>Activos</th><th>Ganados</th><th>Perdidos</th><th>Conversión</th></tr></thead>
              <tbody>
                {conversion.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{c.name}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{c.source_type}</td>
                    <td style={{ color: 'var(--text-primary)' }}>{c.total}</td>
                    <td style={{ color: 'var(--text-primary)' }}>{c.activos}</td>
                    <td style={{ color: '#22c55e', fontWeight: 600 }}>{c.ganados}</td>
                    <td style={{ color: '#ef4444' }}>{c.perdidos}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 60, height: 6, background: 'var(--bg-secondary)', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${c.conversion_rate || 0}%`, height: '100%', background: '#22c55e', borderRadius: 3 }} />
                        </div>
                        <span style={{ fontWeight: 600, color: '#22c55e' }}>{c.conversion_rate || 0}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p style={{color:'var(--text-muted)'}}>Sin datos</p>}
      </div>
    </div>
  );
}
